import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Single image uploader. Calls POST /api/uploads, returns absolute URL via onChange.
 */
export default function ImageUploadField({
  value,
  onChange,
  label,
  testId = "image-upload",
  aspect = "aspect-video",
}) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Only JPG/PNG/WEBP");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div>
      {label && (
        <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{label}</label>
      )}
      <div className={`relative mt-1 overflow-hidden rounded-xl border border-border/60 bg-muted/30 ${aspect}`}>
        {value ? (
          <>
            <img
              src={value}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover scale-110 blur-2xl opacity-60"
            />
            <img src={value} alt="" className="relative h-full w-full object-contain" />
            <button
              type="button"
              onClick={() => onChange("")}
              data-testid={`${testId}-clear`}
              className="absolute top-2 right-2 h-7 w-7 rounded-full bg-background/90 border border-border flex items-center justify-center hover:bg-background z-10"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">
            No image
          </div>
        )}
      </div>
      <input
        ref={ref}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={pick}
        data-testid={`${testId}-input`}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="mt-2 rounded-full"
        disabled={busy}
        onClick={() => ref.current?.click()}
        data-testid={`${testId}-trigger`}
      >
        {busy ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Uploading…</> : <><Upload className="h-3.5 w-3.5 mr-1" /> Upload</>}
      </Button>
    </div>
  );
}
